/**
 * DrawGestureEngine — the reusable click-to-add polyline / polygon draw
 * gesture, extracted from ConstructionTool (#804 IE1). A tool OWNS an engine
 * instance and DRIVES it by composition (it is NOT a base class), which
 * sidesteps the inheritance field/method collisions that sank the earlier
 * `DrawTool`-base attempt on the 13k-line ConstructionTool.
 *
 * The engine owns the gesture STATE and the generic FLOW:
 *   - the point buffer + per-vertex payload (bezier handles carried alongside);
 *   - the hover point + rubber-band preview lifecycle (bezier-aware);
 *   - close-snap (S1 `CLOSE_SNAP_RADIUS`) and segment rays (S1 `emitSegmentRay`);
 *   - the fly-out / draft contract (`vertexCount`, `handleFinish`,
 *     `handleEscapeDuringDrag`, the draft-change listener).
 *
 * Everything construction-specific is injected through the `DrawGestureHost`
 * the owning tool implements: snap-tier resolution, continuation seeding,
 * self-snap detection, the model commit, and the capability flags. Leather
 * tools (Stitch / Cutout / Shape) will later implement this host with trivial
 * hooks (IE2 / IE3). See docs/draw-tool-architecture.md.
 */
import paper from "paper";
import type { PointerSample } from "../canvas/PointerInput";
import type { Scene } from "../canvas/Scene";
import {
  CLOSE_SNAP_RADIUS,
  closeSnapRadius,
  emitSegmentRay,
} from "./drawGesture";
import { dragToCurveDeadZoneMm } from "./dragToCurveDeadZone";
import { hasHover } from "../canvas/pointerPrecision";

/** Per-vertex bezier payload (#217). `handleIn` / `handleOut` are RELATIVE to
 *  the vertex position (paper.js convention); either may be undefined for a
 *  sharp corner. Non-bezier tools leave both undefined for every vertex. */
export interface DrawVertexHandle {
  handleIn?: paper.Point;
  handleOut?: paper.Point;
}

/**
 * Everything the engine needs from its owning tool. ConstructionTool
 * implements this with the full construction machinery (bezier / constraints /
 * continuation / custom snap tiers); leather tools will implement it with
 * trivial vertex+grid snapping and no continuation.
 */
export interface DrawGestureHost {
  /** The paper.js scope the draft geometry lives in. */
  getScope(): paper.PaperScope;
  /** The scene, for guide-ray emit / clear. */
  getScene(): Scene;
  /** Stroke colour for the rubber-band preview. */
  previewColor(): string;

  /** True when the active kind closes back to the start (polygon). */
  isPolygon(): boolean;
  /** True while the active draw kind is a polyline / polygon. */
  isPolyKind(): boolean;

  /** Capability: seed / commit-replace an existing open line (#328). */
  readonly continuationEnabled: boolean;
  /** Capability: per-vertex bezier handle pen-drag (#217). */
  readonly bezierEnabled: boolean;
  /** Preset angle step for the pending segment's ray labels (#937) —
   *  optional; only the construction polyline supplies one. */
  pendingAngleStepDeg?(): number | null;
  /** Which angle reference the pending segment actually snapped against
   *  (#938) — optional; the construction polyline reports the Angle step's
   *  winning candidate so the ray shows exactly ONE angle marker. */
  pendingAngleRef?(): "horizontal" | "corner" | null;
  /** The existing line the pending segment's angle was measured from (#162),
   *  as a point along it from the segment's start — optional. Used as the
   *  angle arm on the FIRST segment of a draft, which has no previous edge to
   *  measure a corner against; later segments keep showing the corner, which
   *  is the more useful number once the shape is under way. */
  pendingAngleReferenceArm?(
    from: paper.Point,
    to: paper.Point,
  ): { x: number; y: number } | null;

  /** #328 — if the empty-draft click landed on an existing open line's
   *  endpoint, load its vertices/handles into the engine and remember the
   *  continuation target (`continuingLineId`). Returns true when it seeded. */
  seedContinuation(pt: paper.Point): boolean;

  /** Resolve a placed-vertex position from the raw pointer through the tool's
   *  snap tiers, recording any draft-time snap for the commit. Called on
   *  `onDown` right before the vertex is pushed. */
  resolveDraftPlacement(
    s: PointerSample,
    p: { x: number; y: number },
  ): paper.Point;

  /** Resolve the rubber-band hover position through the tool's snap tiers,
   *  updating any hover rings / grid dots. Called on `onHover` with ≥1
   *  vertex already placed. */
  resolveDraftHover(s: PointerSample, p: { x: number; y: number }): paper.Point;

  /** Handle an `onHover` while the draft is still empty (first-click snap
   *  ring preview). */
  onEmptyDraftHover(s: PointerSample, p: { x: number; y: number }): void;

  /** Nearest previously-placed vertex on this draft within snap range, for
   *  the preview's mid-point halo. Null when none / snapping is off. */
  selfSnapAt(pt: paper.Point): { pos: paper.Point; index: number } | null;

  /** Commit the current draft into the model. Reads the engine's
   *  `points` / `handles` / `continuingLineId`. */
  commitDraft(closed: boolean): void;

  /** Full draft reset (also resets the tool's own drag/offset state). The
   *  engine calls this from `handleFinish` when a sub-2-vertex draft is
   *  abandoned. */
  cancelDraft(): void;

  /** Optional: build the rubber-band guide (and any close halo) into the
   *  engine-owned preview `group`. When a host implements this, the engine
   *  still owns the group lifecycle + `previewItem` but delegates the drawn
   *  geometry, so a tool can render its own preview style (e.g. the leather
   *  tools' thin dashed outline) instead of the construction default (thick
   *  dashed guide + per-vertex dots / bezier tangents / self-snap halos).
   *  `closing` is true while the cursor is inside the close radius of the
   *  first vertex. Leave undefined to keep the construction default. */
  buildPreview?(group: paper.Group, closing: boolean): void;

  /** Optional: true while the active kind is a single LINE (#1113). It draws
   *  exactly like a polyline — click the start, click the end, drag at the end
   *  to curve it — but ends as soon as its one segment exists, with no finish
   *  confirmation. The host commits it by calling `maybeFinishSingleSegment`
   *  on release. */
  finishesAfterOneSegment?(): boolean;
}

export class DrawGestureEngine {
  /** Placed vertices of the in-progress polyline / polygon. */
  points: paper.Point[] = [];
  /** Per-vertex bezier handles (#217), index-aligned with `points`. */
  handles: DrawVertexHandle[] = [];
  /** Index of the vertex currently being pen-drag shaped, or null. */
  bezierDragVertex: number | null = null;
  /** Rubber-band cursor point (the next segment's tentative end). */
  hoverPt: paper.Point | null = null;
  /** #328 — id of the existing line this draft continues (commit replaces). */
  continuingLineId: string | null = null;
  /** #328 — true when continuation grabbed the source line's START. */
  continuingFromStart = false;
  /** Ephemeral preview group for the in-progress draft. */
  previewItem: paper.Item | null = null;
  /** Vertices placed by clicks on THIS draft — not counting any seeded from
   *  an existing line by a continuation (#328). A single line (#1113) is done
   *  once these add up to one new segment. */
  private placedVertices = 0;
  /** A press by a pointer with no hover (#1116) — a finger, or a stylus on
   *  most tablets. Such a pointer can't show a segment BEFORE it is placed, so
   *  its press only starts one: the drag rubber-bands it like a cursor's hover
   *  would, and the RELEASE places the vertex. `startedDraft` marks the press
   *  that placed a draft's first vertex (or seeded a continuation), whose
   *  release adds the end only if the pointer actually moved. */
  private noHoverPress: {
    startedDraft: boolean;
    at: { x: number; y: number };
  } | null = null;

  private draftChangeListener: ((vertexCount: number) => void) | null = null;

  constructor(private host: DrawGestureHost) {}

  /** Number of vertices in the in-progress draft. */
  get vertexCount(): number {
    return this.points.length;
  }

  setDraftChangeListener(fn: ((vertexCount: number) => void) | null) {
    this.draftChangeListener = fn;
  }

  notifyDraftChange() {
    this.draftChangeListener?.(this.points.length);
  }

  /** Clear all draft state and remove the preview. Called by the host's
   *  `cancelDraft`. */
  reset() {
    this.points = [];
    this.handles = [];
    this.bezierDragVertex = null;
    this.continuingLineId = null;
    this.continuingFromStart = false;
    this.hoverPt = null;
    this.placedVertices = 0;
    this.noHoverPress = null;
    this.cleanupPreview();
  }

  cleanupPreview() {
    if (this.previewItem) {
      this.previewItem.remove();
      this.previewItem = null;
    }
  }

  // ---- pointer flow -------------------------------------------------------

  onDown(s: PointerSample, p: { x: number; y: number }) {
    const scope = this.host.getScope();
    const pt = new scope.Point(p.x, p.y);

    // No hover (#1116): mid-draft, the press doesn't place anything — it is
    // the finger arriving. Show the segment to it, and let the drag adjust it;
    // `onUp` places the vertex where the pointer lifts.
    const noHover = !hasHover(s.pointerType);
    if (noHover && this.points.length > 0) {
      this.noHoverPress = { startedDraft: false, at: { x: p.x, y: p.y } };
      this.onHover(s, p);
      return;
    }
    if (noHover) {
      this.noHoverPress = { startedDraft: true, at: { x: p.x, y: p.y } };
    }

    // First-click continuation hook (#328). For BOTH polyline and polygon:
    // when the draft is empty and the click landed on the start/end vertex
    // of an existing open line, load that line's vertices into the draft and
    // treat the gesture as a continuation. On commit the host REPLACES the
    // existing line.
    if (this.host.continuationEnabled && this.points.length === 0) {
      if (this.host.seedContinuation(pt)) {
        this.hoverPt = this.points[this.points.length - 1]!;
        this.bezierDragVertex = null;
        this.updatePreview();
        this.notifyDraftChange();
        return;
      }
    }

    this.placeVertex(s, p);
  }

  /** Place the next vertex at `p` — close a polygon on its start, or resolve
   *  the position through the host's snap tiers and push it. The body of a
   *  click, shared by the no-hover release (#1116). */
  private placeVertex(s: PointerSample, p: { x: number; y: number }) {
    const scope = this.host.getScope();
    const pt = new scope.Point(p.x, p.y);
    // Polygon-only close-on-start: a click near the first vertex finishes the
    // draft as a closed polygon. Polylines treat the start as a self-snap
    // target (handled by the host's placement resolution) so the user can keep
    // drawing.
    if (this.host.isPolygon() && this.points.length >= 2) {
      const first = this.points[0]!;
      if (first.getDistance(pt) < closeSnapRadius(scope.view?.zoom ?? 1)) {
        this.host.commitDraft(this.points.length >= 3);
        return;
      }
    }

    const snapped = this.host.resolveDraftPlacement(s, p);
    this.points.push(snapped);
    this.placedVertices++;
    // Parallel handle slot for the new vertex starts empty (sharp corner);
    // a drag-after-press in `onMove` populates it.
    this.handles.push({});
    // A pointer with no hover draws its segments by dragging (#1116), so its
    // drag can't also pull a curve handle — curves come from Edit points.
    this.bezierDragVertex =
      this.host.bezierEnabled && hasHover(s.pointerType)
        ? this.points.length - 1
        : null;
    this.hoverPt = snapped;
    this.updatePreview();
    this.notifyDraftChange();
  }

  onMove(s: PointerSample, p: { x: number; y: number }) {
    // No-hover drag (#1116): the drag IS the hover — rubber-band the pending
    // segment to the pointer, with its rays, until the release places it.
    if (this.noHoverPress) {
      this.onHover(s, p);
      return;
    }
    // Pen-tool gesture (#217): if the user kept the pointer down after placing
    // a vertex and is now dragging, set that vertex's bezier handles from the
    // drag direction. `handleOut` points at the cursor; `handleIn` mirrors so
    // the curve passes through the anchor smoothly. Both stored RELATIVE to
    // the vertex per paper.js convention.
    if (this.bezierDragVertex === null) return;
    const idx = this.bezierDragVertex;
    const vertex = this.points[idx];
    const slot = this.handles[idx];
    if (!vertex || !slot) return;
    const scope = this.host.getScope();
    const out = new scope.Point(p.x - vertex.x, p.y - vertex.y);
    // Dead-zone so a stationary press (the plain click-to-place gesture) stays
    // sharp — only an intentional drag commits handles. Pen / touch pointers
    // get a wider, zoom-independent grace zone (#395).
    const deadZone = dragToCurveDeadZoneMm(
      s.pointerType,
      scope.view?.zoom ?? 1,
    );
    if (out.length > deadZone) {
      slot.handleOut = out;
      slot.handleIn = new scope.Point(-out.x, -out.y);
    } else {
      slot.handleOut = undefined;
      slot.handleIn = undefined;
    }
    this.hoverPt = vertex;
    this.updatePreview();
  }

  /** Release ends the bezier-drag for the most-recently-placed vertex. The
   *  handles set during the drag stay on the vertex. */
  clearBezierDrag() {
    if (this.bezierDragVertex !== null) {
      this.bezierDragVertex = null;
    }
  }

  /** Release (#1116): for a pointer with no hover, place the vertex the drag
   *  was previewing — where the pointer lifted. The press that started a draft
   *  already placed its first vertex, so its release adds the end only when
   *  the pointer moved past the dead zone: a drag draws the first segment,
   *  a tap just places the start. Mid-draft, a tap places a vertex where it
   *  lands, as a click would. Returns true when it placed one. Hosts call it
   *  on release for their click-to-add kinds, before
   *  `maybeFinishSingleSegment`. A mouse press never sets any of this. */
  onUp(s: PointerSample, p: { x: number; y: number }): boolean {
    const press = this.noHoverPress;
    this.noHoverPress = null;
    if (!press) return false;
    if (press.startedDraft) {
      const scope = this.host.getScope();
      const deadZone = dragToCurveDeadZoneMm(
        s.pointerType,
        scope.view?.zoom ?? 1,
      );
      if (Math.hypot(p.x - press.at.x, p.y - press.at.y) <= deadZone) {
        return false;
      }
    }
    // Place through the normal flow: the same close-on-start and snap tiers
    // a click gets.
    this.placeVertex(s, p);
    return true;
  }

  /** Commit a single-line draft (#1113) once its one segment exists: the
   *  second vertex of a fresh line, or the first new vertex of a continuation
   *  (#328), whose segment runs from the existing line's end. Called by the
   *  host on RELEASE, after any drag-to-curve at that vertex has finished, so
   *  the curve is part of what commits. Returns true when it committed. */
  maybeFinishSingleSegment(): boolean {
    if (!this.host.finishesAfterOneSegment?.()) return false;
    if (this.bezierDragVertex !== null) return false;
    const segments = this.continuingLineId
      ? this.placedVertices
      : this.placedVertices - 1;
    if (segments < 1) return false;
    this.host.commitDraft(false);
    return true;
  }

  onHover(s: PointerSample, p: { x: number; y: number }) {
    if (this.points.length === 0) {
      // No vertices yet — the host shows an upcoming first-click snap target.
      this.host.onEmptyDraftHover(s, p);
      return;
    }
    const pt = this.host.resolveDraftHover(s, p);
    this.hoverPt = pt;
    // #747: show the pending edge as a length + angle ray. With 2+ vertices,
    // also mark the interior angle to the previous edge.
    const n = this.points.length;
    // One angle marker per pending segment (#938): the host reports which
    // reference the snap used; without a hint, mid-draft segments read the
    // CORNER angle (the shaping info) and first segments the horizontal.
    const prevVertex = n >= 2 ? this.points[n - 2] : undefined;
    // A first segment has no previous edge to measure a corner against, so if
    // the angle came off an existing LINE (#162) that line becomes the arm —
    // drawn as a dashed ghost, since it is a reference and not part of the
    // shape. Once there IS a previous edge the corner wins: mid-shape, the
    // turn is the number worth reading.
    const referenceArm = prevVertex
      ? null
      : (this.host.pendingAngleReferenceArm?.(this.points[n - 1]!, pt) ?? null);
    const cornerPrev = prevVertex ?? referenceArm ?? undefined;
    const angleRef = referenceArm
      ? "corner"
      : (this.host.pendingAngleRef?.() ??
        (cornerPrev ? "corner" : "horizontal"));
    emitSegmentRay(
      this.host.getScene(),
      this.points[n - 1]!,
      { x: pt.x, y: pt.y },
      cornerPrev,
      this.host.pendingAngleStepDeg?.() ?? undefined,
      angleRef,
      referenceArm != null,
    );
    this.updatePreview();
  }

  // ---- finish / discard ---------------------------------------------------

  /** End a polyline / polygon at the last clicked vertex (fly-out confirm /
   *  Enter). */
  handleFinish(): boolean {
    if (this.host.isPolyKind() && this.points.length >= 2) {
      this.host.commitDraft(this.host.isPolygon() && this.points.length >= 3);
      return true;
    }
    if (this.points.length > 0) {
      this.host.cancelDraft();
      return true;
    }
    return false;
  }

  /** Double-click finish: convert to a closed polygon when the kind allows
   *  and there are ≥3 vertices. */
  finishOnDoubleClick(): boolean {
    if (this.host.isPolyKind() && this.points.length >= 2) {
      this.host.commitDraft(this.host.isPolygon() && this.points.length >= 3);
      return true;
    }
    return false;
  }

  /** Escape pressed mid pen-drag (#217): pop the in-progress vertex + its
   *  handle entry, clear the bezier-drag pointer, and leave the rest of the
   *  draft intact. Returns true if there was a drag to abort. */
  handleEscapeDuringDrag(): boolean {
    if (this.bezierDragVertex === null) return false;
    this.points.pop();
    this.handles.pop();
    this.bezierDragVertex = null;
    this.hoverPt = this.points[this.points.length - 1] ?? null;
    this.updatePreview();
    this.notifyDraftChange();
    return true;
  }

  // ---- preview ------------------------------------------------------------

  /** Polygon-only close-on-start highlight: true when the cursor is hovering
   *  inside the close radius of the first vertex. */
  isHoveringToClose(): boolean {
    if (!this.host.isPolygon()) return false;
    if (this.points.length < 3 || !this.hoverPt) return false;
    const first = this.points[0]!;
    return (
      first.getDistance(this.hoverPt) <
      closeSnapRadius(this.host.getScope().view?.zoom ?? 1)
    );
  }

  updatePreview() {
    this.cleanupPreview();
    if (this.points.length === 0) return;
    const scope = this.host.getScope();
    const closing = this.isHoveringToClose();

    const group = new scope.Group();
    group.data = { ephemeral: true };

    // Host-supplied preview (leather tools): the engine still owns the group
    // lifecycle + `previewItem`, but the host draws its own guide + close halo.
    if (this.host.buildPreview) {
      this.host.buildPreview(group, closing);
      this.previewItem = group;
      return;
    }

    // Construction default: thick dashed guide + per-vertex dots, bezier
    // tangents, and mid-point self-snap / close halos.
    const stroke = this.host.previewColor();
    // Re-check self-snap against the live hoverPt so a mid-point halo lights
    // up in lock-step with the cursor (same role the close-target halo plays
    // for the start point).
    const selfSnapHit =
      !closing && this.hoverPt ? this.host.selfSnapAt(this.hoverPt) : null;

    // Build the guide path through paper.Segments so per-vertex bezier handles
    // (#217) render as proper cubic curves mid-draft. Vertices with no handles
    // emit straight segments — backward-compatible with the pre-#217 shape.
    const guideSegments: paper.Segment[] = [];
    for (let i = 0; i < this.points.length; i++) {
      const v = this.points[i]!;
      const h = this.handles[i] ?? {};
      guideSegments.push(new scope.Segment(v, h.handleIn, h.handleOut));
    }
    if (!closing && this.hoverPt) {
      const last = this.points[this.points.length - 1];
      if (!last || last.getDistance(this.hoverPt) > 0.5) {
        guideSegments.push(new scope.Segment(this.hoverPt));
      }
    }
    if (guideSegments.length >= 2) {
      const guide = new scope.Path({ insert: false });
      for (const seg of guideSegments) guide.add(seg);
      if (closing) guide.closed = true;
      guide.strokeColor = new scope.Color(stroke);
      guide.strokeWidth = 4;
      guide.strokeScaling = false;
      guide.dashArray = [2, 1.5];
      guide.fillColor = null;
      group.addChild(guide);
    }

    for (let i = 0; i < this.points.length; i++) {
      const v = this.points[i]!;
      const h = this.handles[i] ?? {};
      const isCloseTarget = closing && i === 0;
      const isSelfSnapTarget = !!selfSnapHit && selfSnapHit.index === i;
      if (isCloseTarget || isSelfSnapTarget) {
        // Re-use the close-target halo geometry for mid-point self-snap too
        // so the visual vocabulary stays consistent: "this is the vertex your
        // next click will land on."
        const haloRadius = isCloseTarget ? CLOSE_SNAP_RADIUS : 2.4;
        const halo = new scope.Path.Circle(v, haloRadius);
        halo.strokeColor = new scope.Color(stroke);
        halo.strokeColor.alpha = 0.6;
        halo.strokeWidth = 1.2;
        halo.strokeScaling = false;
        halo.fillColor = null;
        group.addChild(halo);
      }
      const dot = new scope.Path.Circle(
        v,
        isCloseTarget || isSelfSnapTarget ? 1.6 : 0.8,
      );
      dot.fillColor = new scope.Color(stroke);
      dot.strokeWidth = 0;
      group.addChild(dot);
      // Tangent indicators (#217): for vertices with bezier handles, draw a
      // thin line from the anchor to each handle tip plus a small dot at the
      // tip — the classic pen-tool affordance.
      if (h.handleOut) {
        this.appendHandleIndicator(group, v, h.handleOut, scope, stroke);
      }
      if (h.handleIn) {
        this.appendHandleIndicator(group, v, h.handleIn, scope, stroke);
      }
    }

    this.previewItem = group;
  }

  /** Draw one bezier-handle indicator (tangent line + tip dot). `handle` is
   *  relative to `anchor`, matching paper.js convention. */
  private appendHandleIndicator(
    group: paper.Group,
    anchor: paper.Point,
    handle: paper.Point,
    scope: paper.PaperScope,
    stroke: string,
  ): void {
    const tip = new scope.Point(anchor.x + handle.x, anchor.y + handle.y);
    const line = new scope.Path({ segments: [anchor, tip], insert: false });
    line.strokeColor = new scope.Color(stroke);
    line.strokeColor.alpha = 0.6;
    line.strokeWidth = 0.6;
    line.strokeScaling = false;
    line.fillColor = null;
    group.addChild(line);
    const dot = new scope.Path.Circle(tip, 0.7);
    dot.fillColor = new scope.Color(stroke);
    dot.strokeWidth = 0;
    group.addChild(dot);
  }
}
